import dayjs from 'dayjs'
import { PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react'
import { DateRange, PropsRange as DayPickerRangeProps } from 'react-day-picker'
import { LuX } from 'react-icons/lu'

import { ReportFormat, SmartShareReportType } from '@/feature/reports/types/Report'

import { DatePicker } from '@/lib/core/components/DatePicker'
import { ModalBase } from '@/lib/core/components/Modal'
import { Radiomark } from '@/lib/core/components/Radiomark'
import { SHORT_DATE_FORMAT } from '@/lib/core/constants/constants'

import styles from './ReportModal.module.css'

const DEFAULT_SYMBOL = Symbol('default')
export type ReportModalProps = PropsWithChildren<{
  open: boolean
  title: ReactNode
  toDateLimit?: Date
  fromDateLimit?: Date
  onClose: () => void
  onRequest: (options: {
    from: Date
    to: Date | undefined
    format: ReportFormat
    state?: SmartShareReportType
  }) => void
  pickerProps?: Partial<DayPickerRangeProps>
  defaultDateRange?: DateRange | typeof DEFAULT_SYMBOL
  overrideStyle?: {
    readonly [key: string]: string
  }
  canSelectSmartShareReportType?: boolean
  disableSelectDate?: boolean
}>

export const ReportModal = ({
  open,
  title,
  onClose,
  onRequest,
  pickerProps,
  defaultDateRange = DEFAULT_SYMBOL,
  overrideStyle,
  canSelectSmartShareReportType,
  toDateLimit,
  fromDateLimit,
  disableSelectDate,
}: ReportModalProps) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    defaultDateRange === DEFAULT_SYMBOL
      ? {
          from: new Date(),
          to: new Date(),
        }
      : defaultDateRange,
  )

  const iStyles = overrideStyle ? overrideStyle : styles

  useEffect(() => {
    if (defaultDateRange !== DEFAULT_SYMBOL) {
      setDateRange(defaultDateRange)
    }
  }, [defaultDateRange])

  const [selectedFormat, setSelectedFormat] = useState<ReportFormat>(ReportFormat.EXCEL)
  const [runForSmartShareType, setRunForSmartShareType] = useState<
    SmartShareReportType | undefined
  >(undefined)

  const [dateRangeError, setDateRangeError] = useState<{ to?: boolean; from?: boolean }>({
    to: false,
    from: false,
  })

  useEffect(() => {
    if (canSelectSmartShareReportType) {
      setRunForSmartShareType(SmartShareReportType.ACTIVE)
    }
  }, [canSelectSmartShareReportType])

  const endDateLimit = useMemo(() => {
    if (canSelectSmartShareReportType) {
      if (runForSmartShareType === SmartShareReportType.ACTIVE) {
        return undefined
      } else if (runForSmartShareType === SmartShareReportType.HISTORICAL) {
        return new Date()
      }
    }
    return toDateLimit
  }, [toDateLimit, canSelectSmartShareReportType, runForSmartShareType])

  const startDateLimit = useMemo(() => {
    if (canSelectSmartShareReportType) {
      if (runForSmartShareType === SmartShareReportType.ACTIVE) {
        return dayjs().subtract(7, 'days').startOf('day').toDate()
      } else if (runForSmartShareType === SmartShareReportType.HISTORICAL) {
        return undefined
      }
    }
    return fromDateLimit
  }, [fromDateLimit, canSelectSmartShareReportType, runForSmartShareType])

  const dateFixed =
    endDateLimit !== undefined &&
    startDateLimit !== undefined &&
    dayjs(endDateLimit).isSame(dayjs(startDateLimit))

  const isFixedToday = dateFixed && dayjs().isSame(dayjs(endDateLimit), 'date')

  useEffect(() => {
    setDateRangeError({ to: !dateRange?.to, from: !dateRange?.from })
  }, [dateRange?.from, dateRange?.to])

  useEffect(() => {
    setDateRange(undefined)

    if (runForSmartShareType === SmartShareReportType.HISTORICAL && pickerProps) {
      pickerProps.max = undefined
    }
  }, [runForSmartShareType])

  return (
    <ModalBase isActive={open}>
      {open ? (
        <div className={iStyles.modalBody}>
          <div className={iStyles.header}>{title}</div>
          <div className={iStyles.close}>
            <button className={iStyles.closeButton} type={'button'} onClick={onClose}>
              <LuX />
            </button>
          </div>
          <div className={iStyles.body}>
            {canSelectSmartShareReportType && (
              <div className={iStyles.formats}>
                <div>For:</div>
                {Object.keys(SmartShareReportType).map((type) => (
                  <label className={iStyles.format} key={type}>
                    <input
                      type="radio"
                      className={iStyles.hidden}
                      value={runForSmartShareType}
                      name="runFor"
                      onChange={() => setRunForSmartShareType(type as SmartShareReportType)}
                    />
                    <Radiomark
                      isActive={runForSmartShareType === type}
                      className={iStyles.fixRadiomark}
                    />
                    <div>{type.replaceAll('_', ' ')}</div>
                  </label>
                ))}
              </div>
            )}

            {isFixedToday ? (
              <div>Current status (only for today)</div>
            ) : (
              <>
                <div>Select Date Range</div>
                <div className={iStyles.dateSelection}>
                  <DatePicker
                    {...pickerProps}
                    mode="range"
                    initialDate={{
                      from: dateRange?.from,
                      to: dateRange?.to,
                    }}
                    placeholderFrom={(startDateLimit
                      ? dayjs(startDateLimit)
                      : dayjs().subtract(7, 'days')
                    ).format(SHORT_DATE_FORMAT)}
                    placeholderTo={dayjs(endDateLimit).format(SHORT_DATE_FORMAT)}
                    disabled={
                      Boolean(disableSelectDate) || [
                        { after: endDateLimit! },
                        { before: startDateLimit! },
                        endDateLimit !== undefined && endDateLimit === startDateLimit,
                      ]
                    }
                    startMonth={startDateLimit}
                    endMonth={endDateLimit}
                    onChange={dateFixed ? undefined : setDateRange}
                    hasError={dateRangeError.to || dateRangeError.from}
                    hasErrorFrom={!!dateRangeError?.from}
                    hasErrorTo={!!dateRangeError?.to}
                  />
                </div>
              </>
            )}

            <div className={iStyles.formats}>
              <div>Format:</div>
              {Object.keys(ReportFormat).map((format) => (
                <label className={iStyles.format} key={format}>
                  <input
                    type="radio"
                    className={iStyles.hidden}
                    value={format}
                    name="format"
                    onChange={() => setSelectedFormat(format as ReportFormat)}
                  />
                  <Radiomark
                    isActive={selectedFormat === format}
                    className={iStyles.fixRadiomark}
                  />
                  <div>{format.replaceAll('_', ' ')}</div>
                </label>
              ))}
            </div>
          </div>
          <div className={iStyles.footer}>
            <div className={iStyles.actions}>
              <button type="button" style={{ color: '#404040' }} onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className={iStyles.requestButton}
                disabled={!dateRange?.from || !dateRange?.to}
                onClick={() => {
                  if (dateRange?.from && dateRange?.to) {
                    onRequest({
                      from: dayjs(dateRange.from).startOf('day').toDate(),
                      to: dayjs(dateRange.to).endOf('day').toDate(),
                      format: selectedFormat,
                      state: runForSmartShareType,
                    })
                  } else {
                    setDateRangeError({
                      from: !dateRange?.from,
                      to: !dateRange?.to,
                    })
                  }
                }}
              >
                Request
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ModalBase>
  )
}
